/* global chrome */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';

import RacoonLoading from 'src/components/RacoonLoading';
import { useDecodedToken } from 'src/hooks/useDecodedToken';
import { queryApi } from 'src/api/queryApi';
import { PATHS } from 'src/constants/config';

const formatTopic = (topicsString) => {
  const topics = topicsString.match(/#\w+/g);
  if (topics && topics.length > 0) {
    let topic = topics[0].slice(1);
    topic = topic.replace(/([A-Z])/g, ' $1').trim();
    return topic.charAt(0).toLowerCase() + topic.slice(1).toLowerCase();
  }
  return '';
};

const ClientResponseForm = ({ query, leadID, queryID, expert }) => {
  const [reporterPitchBody, setReporterPitchBody] = useState('');
  const [template, setTemplate] = useState('template1');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [windowFocusHandled, setWindowFocusHandled] = useState(false);
  const [redirected, setRedirected] = useState(false);
  const [extensionExists, setExtensionExists] = useState(false);
  const { decodedToken } = useDecodedToken();
  const navigate = useNavigate();

  const checkExtensionExists = () => {
    return new Promise((resolve) => {
      if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
        chrome.runtime.sendMessage('mjeomidkkofecnhcgfjalbfcmnknmghk', { action: 'ping' }, (response) => {
          if (chrome.runtime.lastError) {
            console.log('Chrome extension check failed:', chrome.runtime.lastError);
            resolve(false);
          } else {
            console.log('Chrome extension found');
            resolve(true);
          }
        });
      } else {
        console.log('Chrome extension not available');
        resolve(false);
      }
    });
  };

  const templates = useMemo(
    () => ({
      template1: `Hi <<reporterFirstName>>,

I hope your day is going well!

I am reaching out on behalf of ${expert?.name}, ${expert?.position} at ${expert?.company} (${expert?.websiteURL}). ${expert?.bio}

${expert?.name} was happy to offer some insight into your query, as can be seen below:
-----------------------------



-----------------------------
Let me know if you have any additional questions, ${expert?.name} is always happy to help!

Thank you very much for your time,

${decodedToken.firstName}`,
      template2: `Hi <<reporterFirstName>>,

I hope your week is going well!

I am reaching out on behalf of ${expert?.name}, ${expert?.position} at ${expert?.company} (${expert?.websiteURL}). ${expert?.bio}

${expert?.name} would be more than happy to help with your story on ${formatTopic(
        query?.topics
      )}. Do you have some more specific questions that I can forward?

Please let me know, as I'd be happy to assist with this!

Thank you very much for your time,

${decodedToken.firstName}`,
    }),
    [expert, query?.topics, decodedToken.firstName]
  );

  useEffect(() => {
    setReporterPitchBody(templates[template]);
  }, [template, templates]);

  // Add event listener for when the window regains focus
  useEffect(() => {
    const handleWindowFocus = () => {
      // Only show the dialog if we haven't handled this focus event yet
      // and we're not currently loading
      if (redirected && !windowFocusHandled && !isLoading) {
        setShowConfirmationDialog(true);
        setWindowFocusHandled(true);
      }
    };

    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [windowFocusHandled, isLoading, redirected]);

  useEffect(() => {
    // Check for extension on component mount
    const checkForExtension = async () => {
      const result = await checkExtensionExists();
      setExtensionExists(result);
    };

    checkForExtension();
  }, []);

  const handleTemplateChange = (event) => {
    setTemplate(event.target.value);
    setReporterPitchBody(templates[event.target.value]);
  };

  const generateReporterPitch = async () => {
    setIsLoading(true);

    try {
      const hasExtension = await checkExtensionExists();

      if (hasExtension) {
        const storageKey = `pitch_${queryID}_${Date.now()}`;
        const currentUrl = window.location.href;

        await chrome.runtime.sendMessage('mjeomidkkofecnhcgfjalbfcmnknmghk', {
          action: 'savePitch',
          data: {
            [storageKey]: {
              copiedText: reporterPitchBody,
              queryID: queryID,
              userID: decodedToken?.userID,
              leadID: leadID,
              expertName: expert?.name,
            },
            returnUrl: currentUrl,
          },
        });

        queryApi.updateLead(leadID, undefined, undefined, reporterPitchBody);

        const qwotedUrl = `${query.queryURL}/?storageKey=${storageKey}`;

        setRedirected(true);

        window.open(qwotedUrl, '_blank');

        toast.success('Opening Qwoted...');

        // Reset the window focus handled state so we can show the dialog when the user returns
        setWindowFocusHandled(false);
      } else {
        toast.error('Chrome extension not available');
      }
    } catch (error) {
      toast.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePitchBodyChange = (event) => {
    setReporterPitchBody(event.target.value);
  };

  const handleConfirmationYes = async () => {
    try {
      setIsLoading(true);
      const response = await queryApi.updateLead(leadID, undefined, undefined, reporterPitchBody);
      console.log('send to query response', response);
      const result = await queryApi.updateQuery(queryID, 'pending', undefined, leadID, expert?.id);
      if (result.status === 'Query Updated') {
        toast.success('Query status updated to pending');
        navigate(PATHS.PITCH_INBOX.value + '?status=pending');
      } else {
        toast.error('Failed to update query status');
      }
    } catch (error) {
      toast.error('Error updating query status');
      console.error(error);
    } finally {
      setIsLoading(false);
      setShowConfirmationDialog(false);
    }
  };

  const handleConfirmationNo = () => {
    setShowConfirmationDialog(false);
  };

  const navigateToQuery = () => {
    window.open(query.queryURL, '_blank');
  };

  return (
    <Box sx={{ p: 2 }}>
      {query?.status === 'draft' && (
        <Alert severity="warning" sx={{ border: '1px solid #ff9800', mb: 2 }}>
          Warning: You are in draft stage. Only use this tab if you want to skip the expert email step (e.g., for interview inquiries).
        </Alert>
      )}
      {isLoading && <RacoonLoading />}
      <FormControl component="fieldset">
        <FormLabel component="legend">Select Template</FormLabel>
        <RadioGroup aria-label="template" name="template" value={template} onChange={handleTemplateChange} row>
          <FormControlLabel value="template1" control={<Radio />} label="Insights" />
          <FormControlLabel value="template2" control={<Radio />} label="Interview" />
        </RadioGroup>
      </FormControl>
      <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
        Reporter Pitch
      </Typography>
      <TextField
        id="pitch-body"
        label="Pitch Body"
        multiline
        fullWidth
        rows={24}
        value={reporterPitchBody}
        onChange={handlePitchBodyChange}
        variant="outlined"
      />
      {extensionExists ? (
        <Button variant="contained" onClick={generateReporterPitch} sx={{ mt: 2 }}>
          Send Pitch to Reporter
        </Button>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button variant="contained" onClick={() => navigateToQuery()}>
            1. Navigate to Query
          </Button>
          <Button variant="outlined" onClick={() => setShowConfirmationDialog(true)}>
            2. Mark as Sent to Reporter
          </Button>
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmationDialog}
        onClose={handleConfirmationNo}
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-description"
      >
        <DialogTitle id="confirmation-dialog-title">Pitch Submission Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirmation-dialog-description">Were you able to successfully submit the pitch to the reporter?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmationNo} color="primary">
            No
          </Button>
          <Button onClick={handleConfirmationYes} color="primary" variant="contained" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

ClientResponseForm.propTypes = {
  query: PropTypes.shape({
    status: PropTypes.string,
    queryURL: PropTypes.string,
    topics: PropTypes.string,
  }).isRequired,
  leadID: PropTypes.number.isRequired,
  queryID: PropTypes.number.isRequired,
  expert: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    position: PropTypes.string,
    company: PropTypes.string,
    websiteURL: PropTypes.string,
    bio: PropTypes.string,
  }).isRequired,
};

export default ClientResponseForm;
